import { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { BRAND } from "@/lib/eraContent";
import { useLang } from "@/lib/LanguageContext";
import { cn } from "@/lib/utils";

/**
 * Premium floating WhatsApp button.
 * - Not a generic bright green blob. Uses navy → deep-green gradient with gold ring accent.
 * - Circular, soft shadow, subtle hover lift & pulse.
 * - Opens a WhatsApp chat in a new tab.
 */
export const WhatsAppFloat = () => {
  const { t, lang } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const message =
    lang === "id"
      ? "Halo ERA Group, saya tertarik untuk mengetahui lebih lanjut tentang layanan Anda."
      : "Hello ERA Group, I'd like to learn more about your services.";
  const href = `https://wa.me/${BRAND.phoneDigits}?text=${encodeURIComponent(message)}`;

  return (
    <div
      className={cn(
        "fixed z-40 bottom-6 right-6 sm:bottom-8 sm:right-8 transition-all duration-500",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none",
      )}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-btn"
        aria-label={t.contact.whatsappTooltip}
        className="group relative flex items-center justify-center h-14 w-14 rounded-full bg-navy-900 text-white shadow-[0_18px_40px_-12px_rgba(13,27,62,0.55)] ring-1 ring-white/10 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_50px_-12px_rgba(13,27,62,0.7)]"
      >
        {/* Radial gradient sheen */}
        <span className="absolute inset-0 bg-gradient-to-br from-emerald-500/80 via-emerald-600/40 to-transparent opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
        <span className="absolute -inset-0.5 rounded-full bg-gradient-to-tr from-gold/0 via-gold/40 to-gold/0 opacity-0 blur-sm transition-opacity duration-500 group-hover:opacity-100" />

        {/* Soft pulse ring */}
        <span className="absolute inset-0 rounded-full ring-2 ring-emerald-400/30 animate-ping group-hover:ring-emerald-400/50" />

        {/* Icon */}
        <MessageCircle className="relative w-6 h-6 stroke-[2] drop-shadow-sm" />

        {/* Tooltip */}
        <span
          className="hidden sm:block absolute right-full mr-3 whitespace-nowrap rounded-full bg-navy-900 text-white text-xs font-medium px-3 py-1.5 shadow-lg opacity-0 -translate-x-2 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"
        >
          {t.contact.whatsappTooltip}
        </span>
      </a>
    </div>
  );
};
